import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  Divider,
  InputAdornment,
  Card,
  CardContent,
  alpha,
  useTheme,
  Tooltip,
  CircularProgress,
  FormHelperText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as ShippingIcon,
  Inventory as InventoryIcon,
  Store as StoreIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useAuth } from "../../../auth/hooks/useAuth";
import { stockTransferService } from "../../services/stock-transfer.service";
import {
  StockTransfer,
  StockTransferFormData,
  StockTransferItemFormData,
  LocationOption,
  ProductOption,
  BatchOption,
  CreateStockTransferRequest,
  UpdateStockTransferRequest,
  TRANSFER_TYPES,
} from "../../types/stock-transfer.types";

interface StockTransferFormProps {
  transfer?: StockTransfer;
  onSave: (transfer: StockTransfer) => void;
  onCancel: () => void;
  mode?: "create" | "edit";
}

export const StockTransferForm: React.FC<StockTransferFormProps> = ({
  transfer,
  onSave,
  onCancel,
  mode = "create",
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState<StockTransferFormData>({
    transfer_type: TRANSFER_TYPES.INTERNAL,
    source_location: null,
    target_location: null,
    expected_delivery_date: null,
    notes: "",
    reason: "",
    items: [],
  });

  // Options state
  const [locationOptions, setLocationOptions] = useState<LocationOption[]>([]);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [batchOptions, setBatchOptions] = useState<{
    [productId: string]: BatchOption[];
  }>({});

  // Loading states
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [productsLoading, setProductsLoading] = useState(false);
  const [batchesLoading, setBatchesLoading] = useState<{
    [productId: string]: boolean;
  }>({});

  // Validation state
  const [errors, setErrors] = useState<string[]>([]);
  const [itemErrors, setItemErrors] = useState<{ [index: number]: string[] }>(
    {}
  );

  // Item selection dialog state
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<string[]>([]);

  useEffect(() => {
    loadLocations();
    if (transfer) {
      populateFormFromTransfer();
    }
  }, [transfer]);

  useEffect(() => {
    if (formData.source_location) {
      loadProducts();
    }
  }, [formData.source_location]);

  const loadLocations = async () => {
    setLocationsLoading(true);
    try {
      if (!user || !user.store_id) {
        throw new Error("No store_id found for user");
      }
      const response = await stockTransferService.getLocations(user.store_id);
      if (response.success) {
        const { branches = [], warehouses = [] } = response.data;
        // Normalize to LocationOption shape
        const normalize = (loc: any) => ({
          id: loc.id,
          _id: loc._id || loc.id, // Add _id for backend compatibility
          name: loc.name,
          branch_id: loc.branch_id || "",
          branch_name: loc.branch_name || "",
          warehouse_id: loc.warehouse_id || loc.id || "",
          warehouse_name: loc.warehouse_name || loc.name || "",
        });
        const allLocations = [
          ...branches.map(normalize),
          ...warehouses.map(normalize),
        ];
        setLocationOptions(allLocations);
      }
    } catch (error) {
      console.error("Error loading locations:", error);
      toast.error("Failed to load locations");
    } finally {
      setLocationsLoading(false);
    }
  };

  const loadProducts = async () => {
    if (!formData.source_location) return;

    setProductsLoading(true);
    try {
      // Use _id if present, otherwise fallback to warehouse_id
      const locationId =
        formData.source_location._id || formData.source_location.warehouse_id;
      const response = await stockTransferService.getTransferableProducts(
        locationId
      );
      if (response.success) {
        setProductOptions(response.data);
      }
    } catch (error) {
      console.error("Error loading products:", error);
      toast.error("Failed to load products");
    } finally {
      setProductsLoading(false);
    }
  };

  const loadBatches = async (productId: string) => {
    if (!formData.source_location) return;

    setBatchesLoading((prev) => ({ ...prev, [productId]: true }));
    try {
      const response = await stockTransferService.getProductBatches(
        productId,
        formData.source_location.warehouse_id
      );
      if (response.success) {
        setBatchOptions((prev) => ({ ...prev, [productId]: response.data }));
      }
    } catch (error) {
      console.error("Error loading batches:", error);
      toast.error("Failed to load batches");
    } finally {
      setBatchesLoading((prev) => ({ ...prev, [productId]: false }));
    }
  };

  const populateFormFromTransfer = () => {
    if (!transfer) return;

    setFormData({
      transfer_type: transfer.transfer_type,
      source_location: {
        id: transfer.source_location.warehouse_id,
        name: transfer.source_location.warehouse_name,
        branch_id: transfer.source_location.branch_id,
        branch_name: transfer.source_location.branch_name,
        warehouse_id: transfer.source_location.warehouse_id,
        warehouse_name: transfer.source_location.warehouse_name,
      },
      target_location: {
        id: transfer.target_location.warehouse_id,
        name: transfer.target_location.warehouse_name,
        branch_id: transfer.target_location.branch_id,
        branch_name: transfer.target_location.branch_name,
        warehouse_id: transfer.target_location.warehouse_id,
        warehouse_name: transfer.target_location.warehouse_name,
      },
      expected_delivery_date: transfer.expected_delivery_date || null,
      notes: transfer.notes || "",
      reason: transfer.reason || "",
      items: transfer.items.map((item) => ({
        product: {
          id: item.product_id,
          name: item.item_name,
          sku: item.sku,
          unit: item.unit,
          available_quantity: item.available_quantity,
          is_batch_tracked: item.is_batch_tracked,
        },
        quantity: item.quantity,
        batch: item.batch_id
          ? {
              batch_id: item.batch_id,
              batch_number: item.batch_number || "",
              quantity: item.quantity,
              remaining_quantity: item.available_quantity,
              expiry_date: item.expiry_date,
              unit_cost: item.unit_cost,
              total_cost: item.total_cost,
            }
          : null,
        unit: item.unit,
        unit_cost: item.unit_cost,
        available_quantity: item.available_quantity,
        max_transferable: item.max_transferable,
        errors: [],
      })),
    });
  };

  const handleTransferTypeChange = (type: "Inter-Branch" | "Internal") => {
    setFormData((prev) => ({
      ...prev,
      transfer_type: type,
      source_location: null,
      target_location: null,
      items: [],
    }));
    setProductOptions([]);
    setBatchOptions({});
  };

  const handleLocationChange = (
    field: "source_location" | "target_location",
    location: LocationOption | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: location,
    }));

    if (field === "source_location") {
      setFormData((prev) => ({
        ...prev,
        items: [], // Clear items when source changes
      }));
      setProductOptions([]);
      setBatchOptions({});
    }
  };

  const addItem = () => {
    const newItem: StockTransferItemFormData = {
      product: null,
      quantity: 0,
      batch: null,
      unit: "",
      unit_cost: 0,
      available_quantity: 0,
      max_transferable: 0,
      errors: [],
    };

    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, newItem],
    }));
  };

  const removeItem = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
    setItemErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[index];
      return newErrors;
    });
  };

  const handleItemChange = async (
    index: number,
    field: keyof StockTransferItemFormData,
    value: any
  ) => {
    const updatedItems = [...formData.items];
    const item = updatedItems[index];

    if (field === "product") {
      const product = value as ProductOption;
      item.product = product;
      item.unit = product?.unit || "";
      item.available_quantity = product?.available_quantity || 0;
      item.max_transferable = product?.available_quantity || 0;
      item.unit_cost = 0;
      item.batch = null;
      item.errors = [];

      // Load batches if product is batch-tracked
      if (product?.is_batch_tracked) {
        await loadBatches(product.id);
      }
    } else if (field === "batch") {
      const batch = value as BatchOption;
      item.batch = batch;
      item.unit_cost = batch?.unit_cost || 0;
      item.max_transferable = batch?.remaining_quantity || 0;
    } else if (field === "quantity") {
      const quantity = Number(value);
      item.quantity = quantity;
      item.errors = validateItem(item, quantity);
    } else {
      (item as any)[field] = value;
    }

    setFormData((prev) => ({
      ...prev,
      items: updatedItems,
    }));

    // Update item errors
    setItemErrors((prev) => ({
      ...prev,
      [index]: item.errors,
    }));
  };

  const validateItem = (
    item: StockTransferItemFormData,
    quantity: number
  ): string[] => {
    const errors: string[] = [];

    if (!item.product) {
      errors.push("Product is required");
    }

    if (!quantity || quantity <= 0) {
      errors.push("Quantity must be greater than 0");
    }

    if (quantity > item.max_transferable) {
      errors.push(
        `Quantity exceeds available stock (${item.max_transferable})`
      );
    }

    if (item.product?.is_batch_tracked && !item.batch) {
      errors.push("Batch selection is required for batch-tracked items");
    }

    return errors;
  };

  const validateForm = (): { isValid: boolean; errors: string[] } => {
    const validationErrors: string[] = [];

    // Required fields
    if (!formData.transfer_type) {
      validationErrors.push("Transfer type is required");
    }

    if (!formData.source_location) {
      validationErrors.push("Source location is required");
    }

    if (!formData.target_location) {
      validationErrors.push("Target location is required");
    }

    if (!formData.expected_delivery_date) {
      validationErrors.push("Expected delivery date is required");
    }

    // Business rules
    if (formData.source_location && formData.target_location) {
      if (
        formData.source_location.warehouse_id ===
        formData.target_location.warehouse_id
      ) {
        validationErrors.push("Source and target locations must be different");
      }
    }

    if (
      formData.transfer_type === TRANSFER_TYPES.INTERNAL &&
      !formData.reason.trim()
    ) {
      validationErrors.push("Reason is required for internal transfers");
    }

    if (formData.items.length === 0) {
      validationErrors.push("At least one item is required");
    }

    // Validate items
    const itemValidationErrors: { [index: number]: string[] } = {};
    formData.items.forEach((item, index) => {
      const errors = validateItem(item, item.quantity);
      if (errors.length > 0) {
        itemValidationErrors[index] = errors;
        validationErrors.push(`Item ${index + 1}: ${errors.join(", ")}`);
      }
    });

    setErrors(validationErrors);
    setItemErrors(itemValidationErrors);

    return {
      isValid: validationErrors.length === 0,
      errors: validationErrors,
    };
  };

  const handleSubmit = async (status: "draft" | "dispatched" = "draft") => {
    const validation = validateForm();
    if (!validation.isValid) {
      toast.error("Please fix the validation errors before saving");
      return;
    }

    setSaving(true);
    try {
      const transferData:
        | CreateStockTransferRequest
        | UpdateStockTransferRequest = {
        transfer_type: formData.transfer_type,
        source_location: {
          branch_id: formData.source_location!.branch_id,
          warehouse_id: formData.source_location!.warehouse_id,
        },
        target_location: {
          branch_id: formData.target_location!.branch_id,
          warehouse_id: formData.target_location!.warehouse_id,
        },
        items: formData.items.map((item) => ({
          product_id: item.product!.id,
          quantity: item.quantity,
          unit: item.unit,
          batch_id: item.batch?.batch_id,
          unit_cost: item.unit_cost,
        })),
        expected_delivery_date: formData.expected_delivery_date!,
        notes: formData.notes.trim(),
        reason: formData.reason.trim(),
      };

      let response;
      if (mode === "create") {
        response = await stockTransferService.createStockTransfer(
          transferData as CreateStockTransferRequest
        );
      } else {
        response = await stockTransferService.updateStockTransfer(
          transfer!._id,
          transferData as UpdateStockTransferRequest
        );
      }

      if (response.success) {
        toast.success(
          `Transfer ${mode === "create" ? "created" : "updated"} successfully`
        );
        onSave(response.data);
      }
    } catch (error) {
      console.error("Error saving transfer:", error);
      toast.error(
        `Failed to ${mode === "create" ? "create" : "update"} transfer`
      );
    } finally {
      setSaving(false);
    }
  };

  const calculateTotalValue = () => {
    return formData.items.reduce((total, item) => {
      return total + item.quantity * item.unit_cost;
    }, 0);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  const getAvailableTargetLocations = () => {
    if (!formData.source_location) return locationOptions;

    if (formData.transfer_type === TRANSFER_TYPES.INTERNAL) {
      // For internal transfers, show only locations within the same branch
      return locationOptions.filter(
        (location) =>
          location.branch_id === formData.source_location!.branch_id &&
          location.warehouse_id !== formData.source_location!.warehouse_id
      );
    } else {
      // For inter-branch transfers, show locations from different branches
      return locationOptions.filter(
        (location) => location.branch_id !== formData.source_location!.branch_id
      );
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: "100%", maxWidth: 1200, mx: "auto", p: 3 }}>
        <Paper
          elevation={0}
          variant="outlined"
          sx={{ borderRadius: 2, overflow: "hidden" }}
        >
          {/* Header */}
          <Box
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.05),
              p: 3,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <ShippingIcon sx={{ color: "primary.main" }} />
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {mode === "create"
                  ? "Create Stock Transfer"
                  : "Edit Stock Transfer"}
              </Typography>
            </Box>
          </Box>

          {/* Form Content */}
          <Box sx={{ p: 3 }}>
            {/* Error Alert */}
            {errors.length > 0 && (
              <Alert
                severity="error"
                icon={<WarningIcon />}
                sx={{ mb: 3 }}
                onClose={() => setErrors([])}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Please fix the following errors:
                </Typography>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {errors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </Alert>
            )}

            <Grid container spacing={3}>
              {/* Transfer Type */}
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel>Transfer Type</InputLabel>
                  <Select
                    value={formData.transfer_type}
                    onChange={(e) =>
                      handleTransferTypeChange(
                        e.target.value as "Inter-Branch" | "Internal"
                      )
                    }
                    label="Transfer Type"
                  >
                    <MenuItem value={TRANSFER_TYPES.INTERNAL}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <StoreIcon fontSize="small" />
                        Internal (Same Branch)
                      </Box>
                    </MenuItem>
                    <MenuItem value={TRANSFER_TYPES.INTER_BRANCH}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <ShippingIcon fontSize="small" />
                        Inter-Branch
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Expected Delivery Date */}
              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Expected Delivery Date *"
                  value={
                    formData.expected_delivery_date
                      ? dayjs(formData.expected_delivery_date)
                      : null
                  }
                  onChange={(date) =>
                    setFormData((prev) => ({
                      ...prev,
                      expected_delivery_date: date
                        ? (date as any).valueOf()
                        : null,
                    }))
                  }
                  minDate={dayjs()}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={
                        !formData.expected_delivery_date &&
                        errors.some((e) => e.includes("delivery date"))
                      }
                    />
                  )}
                />
              </Grid>

              {/* Source Location */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={locationOptions}
                  getOptionLabel={(option) =>
                    `${option.branch_name} - ${option.warehouse_name}`
                  }
                  value={formData.source_location}
                  onChange={(_, value) =>
                    handleLocationChange("source_location", value)
                  }
                  loading={locationsLoading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Source Location *"
                      error={
                        !formData.source_location &&
                        errors.some((e) => e.includes("Source location"))
                      }
                      helperText={
                        !formData.source_location &&
                        errors.some((e) => e.includes("Source location"))
                          ? "Source location is required"
                          : ""
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <InventoryIcon />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Target Location */}
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={getAvailableTargetLocations()}
                  getOptionLabel={(option) =>
                    `${option.branch_name} - ${option.warehouse_name}`
                  }
                  value={formData.target_location}
                  onChange={(_, value) =>
                    handleLocationChange("target_location", value)
                  }
                  loading={locationsLoading}
                  disabled={!formData.source_location}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Target Location *"
                      error={
                        !formData.target_location &&
                        errors.some((e) => e.includes("Target location"))
                      }
                      helperText={
                        !formData.target_location &&
                        errors.some((e) => e.includes("Target location"))
                          ? "Target location is required"
                          : ""
                      }
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <InputAdornment position="start">
                              <InventoryIcon />
                            </InputAdornment>
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Reason (for internal transfers) */}
              {formData.transfer_type === TRANSFER_TYPES.INTERNAL && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Reason for Transfer *"
                    value={formData.reason}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        reason: e.target.value,
                      }))
                    }
                    multiline
                    rows={2}
                    error={
                      formData.transfer_type === TRANSFER_TYPES.INTERNAL &&
                      !formData.reason.trim()
                    }
                    helperText={
                      formData.transfer_type === TRANSFER_TYPES.INTERNAL &&
                      !formData.reason.trim()
                        ? "Reason is required for internal transfers"
                        : ""
                    }
                  />
                </Grid>
              )}

              {/* Notes */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, notes: e.target.value }))
                  }
                  multiline
                  rows={3}
                  placeholder="Additional notes or special instructions..."
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            {/* Items Section */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Items to Transfer
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addItem}
                  disabled={!formData.source_location || productsLoading}
                >
                  Add Item
                </Button>
              </Box>

              {formData.items.length === 0 ? (
                <Card variant="outlined" sx={{ textAlign: "center", py: 4 }}>
                  <Typography variant="body1" color="text.secondary">
                    No items added yet. Click "Add Item" to get started.
                  </Typography>
                </Card>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell>Quantity</TableCell>
                        <TableCell>Unit</TableCell>
                        <TableCell>Batch</TableCell>
                        <TableCell>Unit Cost</TableCell>
                        <TableCell>Total Cost</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center">Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Autocomplete
                              options={productOptions}
                              getOptionLabel={(option) =>
                                `${option.name} (${option.sku})`
                              }
                              value={item.product}
                              onChange={(_, value) =>
                                handleItemChange(index, "product", value)
                              }
                              loading={productsLoading}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  placeholder="Select product"
                                  error={itemErrors[index]?.some((e) =>
                                    e.includes("Product")
                                  )}
                                  sx={{ minWidth: 200 }}
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={item.quantity}
                              onChange={(e) =>
                                handleItemChange(
                                  index,
                                  "quantity",
                                  e.target.value
                                )
                              }
                              error={itemErrors[index]?.some((e) =>
                                e.includes("Quantity")
                              )}
                              helperText={`Max: ${item.max_transferable}`}
                              inputProps={{ min: 0, step: 0.01 }}
                              sx={{ width: 100 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{item.unit}</Typography>
                          </TableCell>
                          <TableCell>
                            {item.product?.is_batch_tracked ? (
                              <Autocomplete
                                options={batchOptions[item.product.id] || []}
                                getOptionLabel={(option) =>
                                  `${option.batch_number} (${option.remaining_quantity} ${item.unit})`
                                }
                                value={item.batch}
                                onChange={(_, value) =>
                                  handleItemChange(index, "batch", value)
                                }
                                loading={batchesLoading[item.product.id]}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    size="small"
                                    placeholder="Select batch"
                                    error={itemErrors[index]?.some((e) =>
                                      e.includes("Batch")
                                    )}
                                    sx={{ minWidth: 150 }}
                                  />
                                )}
                              />
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                N/A
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {formatCurrency(item.unit_cost)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 600 }}
                            >
                              {formatCurrency(item.quantity * item.unit_cost)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {itemErrors[index]?.length > 0 ? (
                              <Tooltip title={itemErrors[index].join(", ")}>
                                <Chip
                                  label="Error"
                                  color="error"
                                  size="small"
                                  icon={<WarningIcon />}
                                />
                              </Tooltip>
                            ) : (
                              <Chip
                                label="Valid"
                                color="success"
                                size="small"
                                icon={<CheckCircleIcon />}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton
                              onClick={() => removeItem(index)}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Transfer Summary */}
              {formData.items.length > 0 && (
                <Card variant="outlined" sx={{ mt: 2 }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Total Items
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formData.items.length}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Total Value
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: 600, color: "primary.main" }}
                        >
                          {formatCurrency(calculateTotalValue())}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Transfer Type
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formData.transfer_type}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Typography variant="body2" color="text.secondary">
                          Expected Delivery
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {formData.expected_delivery_date
                            ? dayjs(formData.expected_delivery_date).format(
                                "MMM DD, YYYY"
                              )
                            : "Not set"}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}
            </Box>

            {/* Action Buttons */}
            <Box
              sx={{
                display: "flex",
                gap: 2,
                justifyContent: "flex-end",
                mt: 4,
              }}
            >
              <Button variant="outlined" onClick={onCancel} disabled={saving}>
                Cancel
              </Button>
              <LoadingButton
                variant="contained"
                onClick={() => handleSubmit("draft")}
                loading={saving}
                disabled={formData.items.length === 0}
              >
                {mode === "create" ? "Create Transfer" : "Update Transfer"}
              </LoadingButton>
            </Box>
          </Box>
        </Paper>
      </Box>
    </LocalizationProvider>
  );
};
